/* eslint-disable no-underscore-dangle */
/* eslint-disable no-undef */
import { useCallback, useContext, useEffect } from 'react';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { REDIRECT_PARAM } from '@namespace/common';
import {
  BetLimitNotification,
  IHUB_NOTIFICATION_TYPES
} from '@namespace/games';
import { getRootPath } from '@namespace/helpers';
import { useLocation } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useHistory, useRouteParams, useSearchParams } from '@namespace/router';
import {
  getSmarticoAccessCheck,
  useLogout,
  USER_ROLES,
  UserContext,
  useUserInfoLoaded
} from '@namespace/user';
import { SMARTICO_USER_ERROR_CODE } from '../../constants';

const extractActionType = (smarticoFrame) => {
  return smarticoFrame?.split('/')[1] || '';
};

export const useSmarticoActions = () => {
  const { customNotification } = useNotifications();
  const [user = {}] = useContext(UserContext);
  const { role } = user;
  const { push } = useHistory();
  const isLoggedInUser = role === USER_ROLES.USER;
  const { smarticoDeepLinks = {}, smarticoWidgetSettings = {} } =
    useOtherSettings();
  const { isShowPromoRestricted } = smarticoWidgetSettings;
  const { sFrame: smarticoFrame } = useSearchParams();
  const { login, promotionsWidget } = useGeneralRoutes();
  const { pathname } = useLocation();
  const { logout } = useLogout();
  const smarticoType = extractActionType(smarticoFrame);
  const { userInfoLoaded } = useUserInfoLoaded();
  const redirectToLoginUrl = `${login}?${REDIRECT_PARAM}${pathname}?sFrame=${smarticoFrame}`;

  const { gameCode, provider } = useRouteParams(
    `/:service/show-game/:provider/:gameCode`
  );
  const isGamePage = gameCode || provider;

  const startSmarticoScript = useCallback(() => {
    const deepLink = smarticoDeepLinks[smarticoType];

    if (deepLink) {
      _smartico.dp(deepLink);
    }
  }, [smarticoType, smarticoDeepLinks]);

  useEffect(() => {
    (async () => {
      if (!isLoggedInUser && userInfoLoaded && smarticoFrame) {
        push(redirectToLoginUrl);
        return;
      }

      if (!isLoggedInUser || !userInfoLoaded || !smarticoFrame) {
        return;
      }

      const { errorCode, status: playersStatus } =
        await getSmarticoAccessCheck();

      if (playersStatus === 'error' && errorCode && smarticoFrame) {
        // If the user is a Terrorist / Under sanction, we have to log out that asshole!
        // u can see statuses(errorCode) here https://wiki.betinvest.work/display/API/Smartico+error+codes
        if (errorCode === SMARTICO_USER_ERROR_CODE.TERRORIST) {
          logout();
          return;
        }

        if (isShowPromoRestricted) {
          push(`${promotionsWidget}?sErrorCode=${errorCode}`);
          return;
        }

        if (errorCode === SMARTICO_USER_ERROR_CODE.WITH_LIMIT_MAX_BET) {
          customNotification({
            NotificationComponent: BetLimitNotification,
            timeout: null,
            message: {
              errorCode: IHUB_NOTIFICATION_TYPES.blocked_by_max_bet_limit
            }
          });
          push(pathname);
          return;
        }

        if (isShowPromoRestricted) {
          push(`${promotionsWidget}?sErrorCode=${errorCode}`);
          return;
        }

        push(`/restricted-access?sErrorCode=${errorCode}`);
      } else if (playersStatus === 'success') {
        if (window._smartico) {
          const basePath = getRootPath(pathname);
          const resolvedPath = basePath === '/casino' ? '/' : basePath;
          const goBackPath = isGamePage ? resolvedPath : pathname;

          if (isGamePage) {
            push(`${resolvedPath}?sFrame=${smarticoFrame}`);
            return;
          }

          startSmarticoScript();

          _smartico.on('gf_closing', () => {
            push(goBackPath);
          });
        }
      }
    })();
  }, [
    smarticoFrame,
    isLoggedInUser,
    window._smartico,
    pathname,
    isGamePage,
    userInfoLoaded
  ]);
};
